import useMedia from "@/composables/useMedia";
import useUserMedia from "@/modules/users/composables/useUserMedia";
import { User } from "@/modules/users/interfaces/user";
import { computed, reactive } from "vue";
import { useToast } from "vue-toastification";

const useDocumentsTab = (user: User) => {
  interface FileInput {
    file: File | null;
    expiresAt: string;
  }

  const form: {
    passport: FileInput;
    driverLicense: FileInput;
    id: FileInput;
  } = reactive({
    passport: {
      file: null,
      expiresAt: "",
    },
    driverLicense: {
      file: null,
      expiresAt: "",
    },
    id: {
      file: null,
      expiresAt: "",
    },
  });
  const toast = useToast();

  const {
    userMedia,
    isLoading,
    refetch,
    userMediaCreateAsync,
    userMediaUpdateAsync,
    userMediaDeleteAsync,
  } = useUserMedia({
    params: {
      userId: user.id,
      collections: ["id", "passport", "driverLicense"],
    },
    immediate: true,
  });

  const { mediaDeleteMediaAsync, mediaUpdateMediaAsync } = useMedia({
    params: {
      mediaId: user.id,
      collections: ["id", "passport", "driverLicense"],
    },
    immediate: false,
  });

  const userId = computed(() => user.id);

  const getFileData = (media: string) => {
    if (!userMedia.value) return undefined;
    const mediaItem = userMedia.value.find(
      (item) => item.collectionName === media,
    );
    return mediaItem ? mediaItem : undefined;
  };

  const fileMetadata = computed(() => {
    return {
      id: getFileData("id"),
      passport: getFileData("passport"),
      driverLicense: getFileData("driverLicense"),
    };
  });

  const handleSave = async (targetMediaKey: keyof typeof form) => {
    try {
      const draft = Object.entries(form)
        .filter(
          ([mediaKey, value]) =>
            mediaKey === targetMediaKey && value?.file && value?.expiresAt,
        )
        .map(([key, value]) => ({
          userId: userId.value,
          collection: key,
          media: value.file as File,
          expirationDate: value.expiresAt,
        }));

      if (draft.length === 0) return;

      await Promise.all(
        draft.map(async (media) => {
          await userMediaCreateAsync({ params: { ...media } });
          toast.success("Documentos guardados");
        }),
      );

      form[targetMediaKey].expiresAt = "";
      form[targetMediaKey].file = null;
      await refetch();
    } catch (error) {
      toast.error("Error al guardar documentos");
    }
  };

  const handleUpdate = async (id: number, expiresAt: string) => {
    try {
      await mediaUpdateMediaAsync({
        params: {
          mediaId: id,
          expirationDate: expiresAt,
        },
      });
      toast.success("Documento actualizado");
      await refetch();
    } catch (err) {
      toast.error("Error al actualizar documento");
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await mediaDeleteMediaAsync({ params: { mediaId: id } });
      toast.success("Documento eliminado");
      await refetch();
    } catch (error) {
      toast.error("Error al eliminar documento");
    }
  };

  return {
    form,
    fileMetadata,
    isLoading,
    userId,
    handleSave,
    handleUpdate,
    handleDelete,
  };
};

export default useDocumentsTab;
